import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth';
import api from '@/config/api';

interface Prediction {
  path: string;
  probability: number;
  label: string;
}

async function fetchPreference() {
  const { data } = await api.get('/task-anticipation/preference');
  return data as { mode: string; max_suggestions: number };
}

async function fetchPredictions(currentPath: string): Promise<Prediction[]> {
  const { data } = await api.get('/task-anticipation/predict', {
    params: { current_path: currentPath, app_context: 'portal' },
  });
  return data.predictions ?? [];
}

function recordNavigation(fromPath: string, toPath: string, sessionId?: string) {
  api.post('/task-anticipation/navigate', {
    from_path: fromPath,
    to_path: toPath,
    app_context: 'portal',
    session_id: sessionId,
  }).catch(() => {
    // fire-and-forget
  });
}

export function useTaskAnticipation() {
  const location = useLocation();
  const prevPath = useRef(location.pathname);
  const { user } = useAuthStore();
  const [mode, setMode] = useState<string>('suggest');
  const queryClient = useQueryClient();

  // Fetch user preference on mount
  useEffect(() => {
    if (!user) return;
    fetchPreference()
      .then((p) => setMode(p.mode))
      .catch(() => setMode('suggest'));
  }, [user]);

  // On route change: record navigation + invalidate predictions
  useEffect(() => {
    if (!user) return;
    if (prevPath.current !== location.pathname) {
      recordNavigation(prevPath.current, location.pathname);
      prevPath.current = location.pathname;
    }
  }, [location.pathname, user]);

  // Fetch predictions (re-fetches when path changes)
  const { data: suggestions = [], isLoading } = useQuery({
    queryKey: ['task-anticipation', location.pathname],
    queryFn: () => fetchPredictions(location.pathname),
    enabled: mode !== 'off' && !!user,
    staleTime: 30_000,
  });

  // Prefetch top suggestion if mode is 'preload' or 'both'
  useEffect(() => {
    if ((mode === 'preload' || mode === 'both') && suggestions.length > 0) {
      const topPath = suggestions[0].path;
      queryClient.prefetchQuery({
        queryKey: ['task-anticipation', topPath],
        queryFn: () => fetchPredictions(topPath),
        staleTime: 30_000,
      });
    }
  }, [mode, suggestions, queryClient]);

  return { suggestions, mode, isLoading };
}
